import { useState } from 'react';
import { NodeViewWrapper, type NodeViewProps } from '@tiptap/react';
import { Paperclip, FileX } from 'lucide-react';
import { AttachmentPreviewModal } from '../AttachmentPreviewModal';

/** Short, human label for a mime type. Falls back to the extension. */
function typeLabel(mimeType: string, filename: string): string {
  if (mimeType === 'application/pdf') return 'PDF';
  if (mimeType.startsWith('image/')) return 'IMG';
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return 'XLS';
  if (mimeType.includes('word') || mimeType.includes('document')) return 'DOC';
  if (mimeType.startsWith('text/')) return 'TXT';
  const ext = filename.includes('.') ? filename.split('.').pop() : '';
  return (ext || 'FILE').toUpperCase().slice(0, 4);
}

/**
 * Renders an attached file inline in the document.
 *
 * A chip whose attachment has been deleted still has to render: the id lives
 * in the saved document, so a file removed later must degrade to a visible
 * "deleted" state rather than breaking the pad it sits in.
 */
export function AttachmentChipView({ node }: NodeViewProps) {
  const [open, setOpen] = useState(false);
  const attachmentId = node.attrs.attachmentId as string | null;
  const filename = (node.attrs.filename as string) || 'Attachment';
  const mimeType = (node.attrs.mimeType as string) || '';

  if (!attachmentId) {
    return (
      <NodeViewWrapper as="span" className="attachment-chip attachment-chip-missing">
        <FileX size={13} aria-hidden="true" />
        <span className="attachment-chip-name">{filename} (unavailable)</span>
      </NodeViewWrapper>
    );
  }

  return (
    <NodeViewWrapper as="span" className="attachment-chip">
      <button
        type="button"
        className="attachment-chip-button"
        onClick={() => setOpen(true)}
        title={filename}
      >
        <Paperclip size={13} aria-hidden="true" />
        <span className="attachment-chip-type">{typeLabel(mimeType, filename)}</span>
        <span className="attachment-chip-name">{filename}</span>
      </button>
      {open && (
        <AttachmentPreviewModal
          attachment={{ id: attachmentId, filename, mime_type: mimeType }}
          onClose={() => setOpen(false)}
        />
      )}
    </NodeViewWrapper>
  );
}
